import axios from 'axios';
import Vue from 'vue';
import store from '@/store';
import configApp from '@/utils/appConfig';
import router from "@/router";
import qs from 'qs'
import { featureAccess } from "@/utils/accesses"

export function resolveEssentialBaseUrl(config) {
  const baseUrl = config.baseURL ||
    (store.state.analytics?.activeCoinData?.baseUrl ?? store.state.analytics.coinData.baseUrl) ||
    configApp.VUE_APP_API_BASE_URL

  if (!baseUrl?.includes('essential-api-')) {
    return { baseUrl }
  }

  const essentialCoin = store.state.analytics.essentialCurrencyList?.find(({ baseUrl: coinBaseUrl }) => {
    return coinBaseUrl === baseUrl
  })

  if (!essentialCoin) {
    console.warn('Essential chain not found for baseUrl:', baseUrl)
    return { baseUrl }
  }

  const commonBaseUrl = baseUrl.replace(/-([^-/]+)$/, '')

  return {
    baseUrl: commonBaseUrl,
    key: essentialCoin.key
  }
}

const apiClient = axios.create();

let cancelTokenSource = axios.CancelToken.source(); // Variable to hold the cancel token source

apiClient.interceptors.request.use(config => {
  // List of routes for which requests should not be canceled
  const routesToExcludeCancellation = ['txBrowserIO', 'reset-token', 'entity'];
  const shouldExcludeCancellation = routesToExcludeCancellation.some(keyword =>
    config.url.includes(keyword)
  );

  config.paramsSerializer = p => {
    return qs.stringify(p, {arrayFormat: 'indices'})
  }

  if (featureAccess('UNIFIED_ESSENTIAL_BASEURL')) {
    const { baseUrl, key } = resolveEssentialBaseUrl(config)

    config.baseURL = baseUrl

    if (key !== undefined) {
      config.params = {
        ...config.params,
        chain: key
      }
    }
  } else {
    config.baseURL = config.baseURL ||
      (store.state.analytics?.activeCoinData?.baseUrl ?? store.state.analytics.coinData.baseUrl) ||
      configApp.VUE_APP_API_BASE_URL
  }

  // Generate a unique request key based on the request URL and method
  const requestKey = `${config.baseURL}_${config.url}_${config.method}_${JSON.stringify(config.params)}`

  // Cancel previous request (if any) with the same key
  if (cancelTokenSource && cancelTokenSource[requestKey] && router.currentRoute.name === 'analytics' && !shouldExcludeCancellation) {
    cancelTokenSource[requestKey].cancel('Request canceled');
  }

  if (router.currentRoute.name === 'analytics' && !shouldExcludeCancellation) {
    cancelTokenSource[requestKey] = axios.CancelToken.source(); // Create a new cancel token source for the request
    config.cancelToken = cancelTokenSource[requestKey].token; // Set the cancel token on the request config
  }

  const token = localStorage.getItem('token');
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
}, error => error);

let refreshTokenPromise

apiClient.interceptors.response.use(
    res => {
      if (res?.config?.url === 'users/me' && !res?.data?.success) {
          Vue.toasted.global.error({ message: `${res?.data?.data?.message}` })
          localStorage.removeItem('token')
          window.location.assign('/')
      }
      if (res?.data?.data?.status === 422 && router.currentRoute.name !== 'login')  {
          localStorage.setItem('lastRoute', router.currentRoute.name)
      }
      if (res?.data?.data?.status === 501) {
        Vue.toasted.global.info({ message: `This feature has not been implemented yet for selected blockchain - ${store.state.analytics.coinData.label}` })
        // throw new Error('Not implemented')
      }

      if (res.data && !res.data.status && res.data.data === null) {
        Vue.toasted.global.error({ message: 'API REQUEST ERROR' })
        throw new Error('API REQUEST ERROR')
      }
      // Handle response data
      return res.data;
    },
    async error => {
      if (error?.message === 'Request canceled') {
        return {
          success: false,
          canceled: true,
          data: null
        }
      }
      if (error?.response?.status === 403 && error?.config?.url === 'users/me') {
          Vue.toasted.global.error({ message: `${error?.response?.data?.data?.message || 'Access denied'}` })
          localStorage.removeItem('token')
          setTimeout(() => window.location.assign('/login'), 2000)
          return
      }
      if (error?.response?.status === 401 && error?.config?.url !== 'users/logout') {
          const refreshToken = localStorage.getItem('refreshToken')
          localStorage.removeItem('token')
          localStorage.setItem('lastRoute', router.currentRoute.name)

          if (refreshToken && refreshToken !== 'undefined') {
              if (!refreshTokenPromise) {
                  refreshTokenPromise = store.dispatch('user/resetToken', { refreshToken })
                      .then(() => {
                          refreshTokenPromise = undefined

                          const fullPath = localStorage.getItem('to.fullPath') || router.currentRoute.name

                          return router.push(fullPath)
                      })
                      .catch(() => {
                          window.location.assign('/login')
                      })
              }
          } else {
              window.location.assign('/login')
          }
      }
      // Handle response error
      throw error;
    }
);

export function cancelRequest(requestKey) {
  if (cancelTokenSource && cancelTokenSource[requestKey]) {
    cancelTokenSource[requestKey].cancel('Request canceled');
    delete cancelTokenSource[requestKey];
  }
}

export default apiClient;
